import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import HomeLayout from '../components/HomeLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Trash2, Lock, Sparkles, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { prettyCategory } from './ShopTime';
import JobConfigSettings from '../components/JobConfigSettings';
import PermissionsSettings from '../components/PermissionsSettings';
import EquipmentCatalogSettings from '../components/EquipmentCatalogSettings';
import DataImportSettings from '../components/DataImportSettings';
import { FieldWorkSettings } from '../components/field-work/FieldWorkSettings';
import { useAuth } from '../contexts/AuthContext';
import { LetterheadSettings } from '../components/reports/LetterheadSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [newCat, setNewCat] = useState('');

  const fetchCats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shop-time/categories`);
      setData(res.data);
    } catch {
      toast.error('Failed to load settings');
    }
  }, []);

  useEffect(() => {
    fetchCats();
  }, [fetchCats]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try {
      await axios.post(`${API}/shop-time/categories`, { name: newCat });
      toast.success('Category added');
      setNewCat('');
      fetchCats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Remove "${prettyCategory(name)}"?`)) return;
    try {
      await axios.delete(`${API}/shop-time/categories/${name}`);
      toast.success('Removed');
      fetchCats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const isGold = data?.is_gold_tier;

  return (
    <HomeLayout title="Settings">
      <div className="space-y-6" data-testid="settings-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
          <h3 className="text-2xl font-bold text-slate-900">Settings</h3>
          <p className="text-slate-600 text-sm">Organization-level configuration.</p>
          </div>
          {user?.role === 'management' && <Button asChild variant="outline"><Link to="/settings/notifications" data-testid="settings-notification-preferences"><Bell size={16} className="mr-2" />My notifications</Link></Button>}
        </div>

        {['management', 'field_management'].includes(user?.role) && <LetterheadSettings />}
        <Card className="p-6 bg-white border border-slate-200 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                Shop Time Categories
                {isGold && (
                  <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-full font-semibold flex items-center gap-1">
                    <Sparkles size={12} /> Gold
                  </span>
                )}
              </h4>
              <p className="text-sm text-slate-500 mt-1">
                Defaults are always available. Gold-tier members can add their own.
              </p>
            </div>
          </div>

          {data && (
            <>
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Defaults</p>
                <div className="flex flex-wrap gap-2" data-testid="default-categories">
                  {data.defaults.map((c) => (
                    <span key={c} className="px-3 py-1 bg-slate-100 border border-slate-200 rounded-full text-xs font-medium text-slate-700">
                      {prettyCategory(c)}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Custom Categories ({data.custom.length})
                </p>
                {data.custom.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">None yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="custom-categories">
                    {data.custom.map((c) => (
                      <span
                        key={c}
                        className="px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs font-medium text-amber-800 flex items-center gap-2"
                        data-testid={`custom-cat-${c}`}
                      >
                        {prettyCategory(c)}
                        {isGold && (
                          <button
                            onClick={() => handleDelete(c)}
                            className="text-amber-600 hover:text-red-600"
                            data-testid={`delete-cat-${c}`}
                          >
                            <Trash2 size={11} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {isGold ? (
                <form onSubmit={handleAdd} className="flex gap-2 pt-2 border-t border-slate-200" data-testid="add-category-form">
                  <Input
                    placeholder="e.g. Warranty Work"
                    value={newCat}
                    onChange={(e) => setNewCat(e.target.value)}
                    data-testid="new-category-input"
                  />
                  <Button type="submit" className="bg-slate-900" data-testid="add-category-btn">
                    <Plus size={16} className="mr-1" /> Add
                  </Button>
                </form>
              ) : (
                <div className="pt-3 border-t border-slate-200 bg-amber-50 -mx-6 -mb-6 px-6 py-4 rounded-b-md flex items-start gap-2">
                  <Lock size={16} className="text-amber-700 mt-0.5" />
                  <p className="text-sm text-amber-900">
                    Custom shop-time categories are a <strong>Gold tier</strong> feature.{' '}
                    <a href="/billing" className="underline font-semibold">Upgrade your plan</a> to unlock.
                  </p>
                </div>
              )}
            </>
          )}
        </Card>

        <JobConfigSettings />
        {user?.effective_permissions?.includes('field.calendar') && ['management', 'field_management'].includes(user?.role) && <FieldWorkSettings />}
        <EquipmentCatalogSettings />
        <DataImportSettings />
        <PermissionsSettings />
      </div>
    </HomeLayout>
  );
}
